import type { CityConfig } from "../city-template/types";

export const winchesterConfig: CityConfig = {
  city: "Winchester",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "winchester",
  route: "/winchester",
  title: "Emergency Passport Winchester, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Winchester, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Clark County and eastern Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Winchester, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Winchester and Clark County travelers get their expedited passports as quickly as 24 hours. Serving Mount Sterling, Richmond, and the I-75 corridor. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Winchester%20Kentucky%20Clark%20County%20scenic%20cityscape%20at%20golden%20hour%20with%20eastern%20Kentucky%20rolling%20hills%2C%20charming%20historic%20downtown%20with%20traditional%20southern%20architecture%2C%20warm%20amber%20light%20over%20I-75%20corridor%2C%20clean%20modern%20cityscape%20photography%2C%20lush%20green%20landscape%2C%20no%20visible%20text%20or%20people%2C%20wide%20panoramic%20view&width=1800&height=700&seq=winchester-hero-001&orientation=landscape",
  heroImageAlt: "Winchester Kentucky Clark County with eastern Kentucky rolling hills and historic downtown",
  geoPosition: "37.9901;-84.1797",
  geoPlacename: "Winchester, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 360 miles from Winchester via I-75",
  },
  nearbyCitiesZips: [
    { name: "Winchester", zips: "40391, 40392" },
    { name: "Lexington", zips: "40502, 40503, 40504, 40505, 40506, 40507, 40508, 40509, 40510, 40511, 40513, 40514, 40515, 40516, 40517, 40526, 40533, 40536, 40544, 40546, 40550, 40555, 40574, 40575, 40576, 40577, 40578, 40579, 40580, 40581, 40582, 40583, 40588, 40591, 40598" },
    { name: "Mount Sterling", zips: "40353" },
    { name: "Richmond", zips: "40475, 40476" },
    { name: "Paris", zips: "40361, 40362" },
    { name: "Stanton", zips: "40380" },
    { name: "Clay City", zips: "40312" },
    { name: "Irvine", zips: "40336" },
    { name: "Berea", zips: "40403, 40404, 40405, 40406, 40461, 40468, 40476, 40488, 40489, 40490, 40492, 40461" },
    { name: "Morehead", zips: "40351" },
    { name: "Camargo", zips: "40336" },
    { name: "Jeffersonville", zips: "40337" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Clark County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lexington", route: "/lexington" },
    { name: "Fast passport renewal in Richmond", route: "/richmond" },
    { name: "Emergency passport help in Mount Sterling", route: "/mount-sterling" },
    { name: "Urgent passport services in Morehead", route: "/morehead" },
    { name: "Same-day passport help in Paris", route: "/paris" },
    { name: "Passport courier services in Berea", route: "/cities/berea" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Winchester remotely via secure courier. We do not have a walk-in office in Winchester — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Clark County travelers.",
  fpcOfficeDistance: "Serving all of Clark County and eastern Kentucky",
  serviceAreaIntro: "From historic downtown Winchester to the I-75 corridor, from the Clark County Courthouse to the eastern Kentucky hills — Fast Passport Center serves the entire Winchester community. We regularly help travelers from Mount Sterling, Richmond, and Paris get their passports fast.",
  nearbyCitiesIntro: "We serve Winchester and all surrounding eastern Kentucky cities including Lexington, Mount Sterling, Richmond, Paris, Stanton, Clay City, Irvine, Berea, Morehead, Camargo, and Jeffersonville. If you are in Clark County, eastern Kentucky, or the I-75 corridor, we have you covered.",
  howItWorksIntro: "Most Winchester travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Winchester and Clark County travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Winchester and eastern Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Winchester? Here are the most common ones we hear from Clark County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Clark County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer P.",
      location: "Winchester, KY",
      rating: 5,
      text: "My passport expired before my cruise to the Bahamas. Fast Passport Center got me a renewal in 5 days. I boarded right on time.",
      service: "Passport Renewal",
    },
    {
      name: "David R.",
      location: "Mount Sterling, KY",
      rating: 5,
      text: "Emergency trip to Mexico for a family emergency. They processed my passport in 48 hours and kept me informed every step.",
      service: "Emergency Passport",
    },
    {
      name: "Angela W.",
      location: "Winchester, KY",
      rating: 5,
      text: "First-time passport for my daughter's exchange program to Japan. They made the process so easy from start to finish.",
      service: "Child Passport",
    },
    {
      name: "Mark S.",
      location: "Winchester, KY",
      rating: 5,
      text: "Lost my passport before a business trip to London. They got me a replacement in 36 hours. I made my meeting.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "winchester-1",
      name: "Winchester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Main St",
      city: "Winchester",
      state: "KY",
      zip: "40391",
      phone: "(859) 744-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Winchester",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Clark County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "winchester-2",
      name: "Clark County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "34 S Main St",
      city: "Winchester",
      state: "KY",
      zip: "40391",
      phone: "(859) 745-0270",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Winchester Courthouse",
      website: "https://www.clarkcountyky.com/",
      websiteLabel: "Clark County Clerk",
      notes: "County-level passport acceptance. Appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "winchester-3",
      name: "Lexington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "760 N Broadway",
      city: "Lexington",
      state: "KY",
      zip: "40508",
      phone: "(859) 252-1522",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from downtown Lexington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the Bluegrass region. High volume — book appointments early.",
      icon: "ri-mail-line",
    },
    {
      id: "winchester-4",
      name: "Fayette County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "162 E Main St",
      city: "Lexington",
      state: "KY",
      zip: "40507",
      phone: "(859) 255-8451",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Lexington Courthouse",
      website: "https://www.fayettecountyky.gov/",
      websiteLabel: "Fayette County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
    {
      id: "winchester-5",
      name: "Mount Sterling Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 S Broadway",
      city: "Mount Sterling",
      state: "KY",
      zip: "40353",
      phone: "(859) 498-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Mount Sterling",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Montgomery County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "winchester-6",
      name: "Richmond Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 N Madison Ave",
      city: "Richmond",
      state: "KY",
      zip: "40475",
      phone: "(859) 623-1111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Madison County. Photo services available nearby.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Clark County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Clark County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Winchester applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Clark County families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Winchester travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Winchester or Lexington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Winchester address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Winchester before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Winchester for travelers with urgent international flights. Our registered couriers hand-deliver documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 6 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Winchester?", a: "Fast Passport Center offers fast passport renewal in Winchester in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "Where are the closest passport acceptance facilities in Winchester?", a: "Popular options include the Winchester Post Office (40391), Clark County Clerk (40391), Lexington Main Post Office (40508), Fayette County Clerk (40507), Mount Sterling Post Office (40353), and Richmond Post Office (40475). All require appointments. We help you find the most convenient location." },
    { q: "Can I expedite a child passport in Winchester?", a: "Yes. Fast Passport Center expedites child passports in Winchester for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "How much does expedited passport service cost in Winchester?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Winchester", address: "1500 Bypass Rd, Winchester, KY 40391", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Winchester", address: "100 Walmart Way, Winchester, KY 40391", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Lexington", address: "3317 Tates Creek Rd, Lexington, KY 40502", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Lexington", address: "235 E Main St, Lexington, KY 40507", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Clark County",
      address: "34 S Main St, Winchester, KY 40391",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Fayette County",
      address: "162 E Main St, Lexington, KY 40507",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "30 miles from Winchester",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "The primary airport for the Bluegrass region. Domestic flights with connections to major international hubs.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: true,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "90 miles from Winchester",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights to Europe, Asia, and South America.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "95 miles from Winchester",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};